import { ActionsUnion, createAction } from "common/reducers/UtilsTypes";
import { IOrgData } from "common/records/Org";
import { IOrgOverviewData } from "common/records/OrgOverview";
import { IdType, Nullable } from "common/types";
import { List } from "immutable";
import { ImageDisplayType } from "common/records/Image";
import { LayoutKind } from "common/records/PreferenceConfig";

export const RECEIVE_ORG = "@org/RECEIVE_ORG";
export const RECEIVE_ORG_OVERVIEWS = "@org/RECEIVE_ORG_OVERVIEWS";
export const SET_CURRENT_ORG_ID = "@org/SET_CURRENT_ORG_ID";
export const FETCH_ORG = "@org/FETCH_ORG";
export const UPDATE_FORM = "@org/UPDATE_FORM";
export const RESET_CURRENT_ORG = "@org/RESET_CURRENT_ORG";
export const SET_ORG_LOADED = "@org/SET_ORG_LOADED";
export const SET_ORG_ERRORS = "@org/SET_ORG_ERRORS";
export const SET_ORG_IMAGE = "@org/SET_ORG_IMAGE";
export const UPDATE_PREF_CONFIG = "@org/UPDATE_PREF_CONFIG";
export const UPDATE_SETTINGS_ACL = "@org/UPDATE_SETTINGS_ACL";
export const UPDATE_PREF_ACL = "@org/UPDATE_PREF_ACL";
export const UPDATE = "@org/UPDATE";
export const REMOVE = "@org/REMOVE";
export const CONCAT = "@org/CONCAT";
export const ON_DRAG_END = "@org/ON_DRAG_END";
export const ON_JOB_MENU_DRAG_END = "@org/ON_JOB_MENU_DRAG_END";
export const ON_DRAG_OVER = "@org/ON_DRAG_OVER";
export const ON_JOB_MENU_DRAG_OVER = "@org/ON_JOB_MENU_DRAG_OVER";
export const CONCAT_DOCUMENT_ORDER = "@org/CONCAT_DOCUMENT_ORDER";
export const ADD_LAYOUT = "@org/ADD_LAYOUT";
export const RENAME_LAYOUT = "@org/RENAME_LAYOUT";
export const TOGGLE_LAYOUT = "@org/TOGGLE_LAYOUT";
export const DELETE_LAYOUT = "@org/DELETE_LAYOUT";
export const ADD_COMMISSION_STEP = "@org/ADD_COMMISSION_STEP";
export const REMOVE_COMMISSION_STEP = "@org/REMOVE_COMMISSION_STEP";
export const EDIT_SLIDE_MODAL = "@org/EDIT_SLIDE_MODAL";
export const ON_CONTRACT_LANGUAGE_DRAG_END = "@org/ON_CONTRACT_LANGUAGE_DRAG_END";
export const ADD_CONTRACT_LANGUAGE = "@org/ADD_CONTRACT_LANGUAGE";

export const OrgActions = {
  setCurrentOrgId: (id: Nullable<number>) => createAction(SET_CURRENT_ORG_ID, { id }),
  receiveOrg: (org: Partial<IOrgData>) => createAction(RECEIVE_ORG, org),
  receiveOrgOverviews: (orgs: IOrgOverviewData[]) => createAction(RECEIVE_ORG_OVERVIEWS, orgs),
  fetchOrg: (id: number) => createAction(FETCH_ORG, id),
  resetOrg: () => createAction(RESET_CURRENT_ORG, {}),
  setOrgLoaded: (orgId: number) => createAction(SET_ORG_LOADED, { orgId }),
  setOrgErrors: (orgId: number, errors: string[]) => createAction(SET_ORG_ERRORS, { orgId, errors }),
  setOrgImage: (imageId: Nullable<number>, display: Nullable<ImageDisplayType>) =>
    createAction(SET_ORG_IMAGE, { imageId, display }),
  updatePrefConfig: (event: { rootPath: (string | number)[]; name: string; value: any }) =>
    createAction(UPDATE_PREF_CONFIG, event),
  updateSettingsAcl: (event: { rootPath: (string | number)[]; name: string; value: any }) =>
    createAction(UPDATE_SETTINGS_ACL, event),
  updatePrefAcl: (event: { name: string; value: List<IdType> }) => createAction(UPDATE_PREF_ACL, event),
  update: (event: { orgPath: (string | number)[]; name: string; value: any }) => createAction(UPDATE, event),
  remove: (event: { orgPath: (string | number)[]; name: string; value: any }) => createAction(REMOVE, event),
  concat: (event: { orgPath: (string | number)[]; name: string; value: List<any> }) => createAction(CONCAT, event),
  onDragEnd: (event: { orgPath: (string | number)[]; name: string; oldIndex: number; newIndex: number }) =>
    createAction(ON_DRAG_END, event),
  onJobMenuDragOver: (event: {
    orgPath: (string | number)[];
    name: string;
    oldSectionIndex: number;
    oldIndex: number;
    newSectionIndex: number;
    newIndex: number;
    newMenuItem: Nullable<string>;
  }) => createAction(ON_JOB_MENU_DRAG_OVER, event),
  onJobMenuDragEnd: (event: {
    orgPath: (string | number)[];
    name: string;
    oldIndex: number;
    section: number;
    newIndex: number;
  }) => createAction(ON_JOB_MENU_DRAG_END, event),
  concatDocumentOrder: (event: { orgPath: (string | number)[]; name: string; ids: List<number> }) =>
    createAction(CONCAT_DOCUMENT_ORDER, event),
  addLayout: (event: {
    orgPath: (string | number)[];
    key: string;
    kind: LayoutKind;
    name: string;
    enabled?: boolean;
  }) => createAction(ADD_LAYOUT, event),
  renameLayout: (event: { orgPath: (string | number)[]; key: string; name: string }) =>
    createAction(RENAME_LAYOUT, event),
  toggleLayout: (event: { orgPath: (string | number)[]; key: string; enabled: boolean }) =>
    createAction(TOGGLE_LAYOUT, event),
  deleteLayout: (event: { orgPath: (string | number)[]; key: string }) => createAction(DELETE_LAYOUT, event),
  addCommissionStep: (orgId: number) => createAction(ADD_COMMISSION_STEP, { orgId }),
  removeCommissionStep: (orgId: number, index: number) => createAction(REMOVE_COMMISSION_STEP, { orgId, index }),
  editSlideModal: (slideIndex: string) => createAction(EDIT_SLIDE_MODAL, { slideIndex }),
  onContractLanguageDragEnd: (oldIndex: number, newIndex: number) =>
    createAction(ON_CONTRACT_LANGUAGE_DRAG_END, { oldIndex, newIndex }),
  addContractLanguage: () => createAction(ADD_CONTRACT_LANGUAGE, {}),
};

export type OrgActions = ActionsUnion<typeof OrgActions>;
